import { AnimatedIconRef } from "components/AnimatedIcon/AnimatedIcon";
import Banner from "components/Banner/Banner";
import {
  GLOBAL_RESULT_FORMAT_KEY,
  ResultFormat,
} from "components/Button/ResultFormatButton/ResultFormatButton";
import { ButtonVariant, CoButton } from "components/CoButton";
import { SEARCH_MODE_ANIMATIONS } from "constants/searchModeConfig";
import { useDynamicConfig } from "features/Statsig";
import { useDeepSearchAnnouncementBanner } from "features/Thread/hooks/useDeepSearchAnnouncementBanner";
import { SearchMode } from "helpers/api";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import { useAppSelector } from "hooks/useStore";
import { PlayIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useRef } from "react";
import DeepSearchAnnouncementVideoImageMobile from "./deep-search-announcement-video-mobile.png";
import DeepSearchAnnouncementVideoImage from "./deep-search-announcement-video.png";
import { DeepSearchIcon } from "design_system";

interface DeepSearchAnnouncementBannerProps {
  onDismiss?: (e: React.MouseEvent) => void;
  onTryIt?: () => void;
}

export const DeepSearchAnnouncementBanner = ({
  onDismiss,
  onTryIt,
}: DeepSearchAnnouncementBannerProps) => {
  const [bannerLabels] = useLabels("features.announcement-banner");
  const isMobile = useAppSelector((state) => state.setting.isMobile);
  const deepSearchConfig = useDynamicConfig("announcement_banner_image_link");
  const { changeSearchMode } = useSearchMode();
  const { isDeepSearchBannerHidden, dismissDeepSearchBanner } =
    useDeepSearchAnnouncementBanner();

  const bannerImageUrl = (deepSearchConfig?.value?.url as string) || "";

  // Ref for animation control
  const animatedIconRef = useRef<AnimatedIconRef>(null);

  // Button hover handlers
  const handleButtonMouseEnter = useCallback(() => {
    if (animatedIconRef.current) {
      animatedIconRef.current.startAnimation();
    }
  }, []);

  const handleButtonMouseLeave = useCallback(() => {
    if (animatedIconRef.current) {
      animatedIconRef.current.stopAnimation();
    }
  }, []);

  const handleBannerOrImageClick = () => {
    if (bannerImageUrl) {
      window.open(bannerImageUrl, "_blank");
    }
  };

  const handleTryItClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent banner click
    changeSearchMode(SearchMode.PRO_RESEARCH);

    // Set global result format to COMPACT when PRO_RESEARCH is selected
    localStorage.setItem(
      GLOBAL_RESULT_FORMAT_KEY,
      JSON.stringify(ResultFormat.COMPACT)
    );

    onTryIt?.();
  };

  const handleDismissClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dismissDeepSearchBanner();
    onDismiss?.(e);
  };

  const imageComponent = (
    <Image
      src={DeepSearchAnnouncementVideoImage}
      alt={bannerLabels["feature"]}
      width={120}
      className="bg-bg-base rounded-lg transition-opacity duration-200 hover:opacity-80 cursor-pointer"
      onClick={handleBannerOrImageClick}
    />
  );

  const actionButtons = (
    <>
      {isMobile && (
        <CoButton
          variant={ButtonVariant.Tertiary}
          label="Watch video"
          icon={<PlayIcon className="w-4 h-4" />}
          size="sm"
          onClick={handleBannerOrImageClick}
          className="mr-2"
          backgroundImage={DeepSearchAnnouncementVideoImageMobile}
        />
      )}
      <CoButton
        variant={ButtonVariant.Tertiary}
        label="Try it"
        icon={<DeepSearchIcon />}
        size="sm"
        onClick={handleTryItClick}
        onMouseEnter={handleButtonMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
        data-testid="deep-search-try-it-button"
      />
    </>
  );

  // Only render if the banner hasn't been explicitly dismissed
  if (isDeepSearchBannerHidden) {
    return null;
  }

  return (
    <div onClick={handleBannerOrImageClick} className="cursor-pointer">
      <Banner
        variant="info"
        title={bannerLabels["title"]}
        customLayout={true}
        image={imageComponent}
        actionButtons={actionButtons}
        onDismiss={handleDismissClick}
      >
        <p className="text-fg-muted md:mr-8">{bannerLabels["description"]}</p>
      </Banner>
    </div>
  );
};

export default DeepSearchAnnouncementBanner;
